import * as _ from "lodash";
import type { OktaAuth } from "@okta/okta-auth-js";
import { jwtDecode } from "jwt-decode";
import axios from "./httpClient";
import { validateOktaToken } from "./validateOktaToken";
import { Log } from "./log";
import { isTokenExpired, redirectToOktaLogin, getUserInfo } from "../helpers";
import { logUserIn } from "../AppState/logUserIn";
import { PopoutNotification } from "../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../AppState/NotificationType";
import type { NavigateFunction } from "react-router-dom";
import type { Dispatch } from "redux";
import {
  TOKEN_CONFIG,
  EXCLUDED_ENDPOINTS,
  DB_USER_ERROR_PATTERNS,
  AUTH_ERROR_MESSAGES,
  STORAGE_KEYS,
  ErrorMessages
} from "../constants";
import { AxiosRequestConfig } from "axios";

// ============================================================================
// State Management
// ============================================================================

let isInterceptorInitialized = false;
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string> | null = null;
let lastTokenValidationTime = 0;

// Store references for use in helper functions
let oktaAuthInstance: OktaAuth | null = null;
let dispatchInstance: Dispatch | null = null;
let navigateInstance: NavigateFunction | null = null;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if JWT token has valid structure and can be decoded
 * @param token - JWT token string
 * @returns true if token is structurally valid, false otherwise
 */
const isTokenStructureValid = (token: string): boolean => {
  if (!token) return false;
  
  try {
    const decoded = jwtDecode(token);
    return decoded !== null && decoded !== undefined;
  } catch (error) {
    console.error("Invalid JWT token structure:", error);
    return false;
  }
};

/**
 * Check if endpoint should skip JWT validation
 * @param url - Request URL
 * @returns true if endpoint is excluded from JWT validation
 */
const isExcludedEndpoint = (url?: string): boolean => {
  if (!url?.includes("/api/v1")) return true;
  return EXCLUDED_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

/**
 * Check if token validation is cached and still fresh
 * @returns true if validation cache is still valid
 */
const isValidationCacheFresh = (): boolean => {
  if (lastTokenValidationTime === 0) return false;
  const timeSinceLastValidation = Date.now() - lastTokenValidationTime;
  return timeSinceLastValidation < TOKEN_CONFIG.VALIDATION_CACHE_DURATION;
};

/**
 * Clear validation cache (called on visibility change or storage events)
 */
const clearValidationCache = (): void => {
  lastTokenValidationTime = 0;
};

/**
 * Check if user is authenticated with Okta
 * @throws Error if user is not authenticated
 */
const ensureOktaAuthenticated = async (): Promise<void> => {
  if (!oktaAuthInstance) {
    throw new Error("OktaAuth instance not initialized");
  }
  const authState = oktaAuthInstance.authStateManager.getAuthState();
  if (!authState?.isAuthenticated) {
    throw new Error(AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED);
  }
};

/**
 * Fetch and store new JWT token with deduplication and retry logic
 * Prevents multiple simultaneous token refresh calls
 * Retries up to MAX_TOKEN_FETCH_RETRIES times before redirecting to login
 * @returns Promise that resolves to JWT token string
 * @throws Error if token fetch fails after all retries
 */

const tryFetchTokenWithRetries = async (): Promise<string> => {
  let lastError: any;
  for (let attempt = 1; attempt <= TOKEN_CONFIG.MAX_FETCH_RETRIES; attempt++) {
    try {
      console.log(`Fetching new JWT token (attempt ${attempt}/${TOKEN_CONFIG.MAX_FETCH_RETRIES})...`);
      await ensureOktaAuthenticated();
      const user = await validateOktaToken();
      localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
      dispatchInstance!(logUserIn(true));
      lastTokenValidationTime = Date.now();
      console.log("Successfully fetched and stored new JWT token");
      return user.jwtToken;
    } catch (error) {
      lastError = error;
      console.error(`Token fetch attempt ${attempt} failed:`, error);
      if (error instanceof Error && error.message === AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED) {
        break;
      }
      if (attempt < TOKEN_CONFIG.MAX_FETCH_RETRIES) {
        console.log(`Retrying in ${TOKEN_CONFIG.FETCH_RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, TOKEN_CONFIG.FETCH_RETRY_DELAY));
      }
    }
  }
  await handleTokenFetchFailure(lastError);
  // handleTokenFetchFailure always throws
  throw lastError ?? new Error("Token fetch failed after all retries");
};

const handleTokenFetchFailure = async (lastError: any): Promise<never> => {
  const errorMsg = `${AUTH_ERROR_MESSAGES.TOKEN_REFRESH_FAILED} (${TOKEN_CONFIG.MAX_FETCH_RETRIES} attempts)`;
  console.error(errorMsg);
  if (oktaAuthInstance) {
    await redirectToOktaLogin(oktaAuthInstance, errorMsg);
  }
  throw lastError ?? new Error("Token fetch failed after all retries");
};

const fetchAndStoreToken = async (): Promise<string> => {
  if (!oktaAuthInstance || !dispatchInstance) {
    throw new Error("Interceptor not properly initialized");
  }
  if (isRefreshingToken && tokenRefreshPromise) {
    console.log("Token refresh already in progress, waiting...");
    return tokenRefreshPromise;
  }
  isRefreshingToken = true;
  tokenRefreshPromise = tryFetchTokenWithRetries();
  try {
    return await tokenRefreshPromise;
  } finally {
    isRefreshingToken = false;
    tokenRefreshPromise = null;
  }
};

/**
 * Get JWT token from localStorage
 * @returns JWT token string or null if not found
 */
const getStoredToken = (): string | null => {
  const authUser = getUserInfo();
  return authUser?.jwtToken ?? null;
};

/**
 * Validate and refresh token if needed
 * Returns valid token ready to use with caching and validation
 * @param requestUrl - URL of the request (for logging purposes)
 * @returns Promise that resolves to valid JWT token string
 * @throws Error if token cannot be validated or refreshed
 */
const ensureValidToken = async (requestUrl: string): Promise<string> => {
  if (!oktaAuthInstance) {
    throw new Error("OktaAuth instance not initialized");
  }

  let token = getStoredToken();

  // Case 1: No token - fetch new one
  if (!token) {
    console.warn(AUTH_ERROR_MESSAGES.TOKEN_MISSING + ". Fetching new token...");
    try {
      token = await fetchAndStoreToken();
    } catch (error) {
      await Log({
        errorMessage: `${AUTH_ERROR_MESSAGES.TOKEN_FETCH_FAILED}, url: ${requestUrl}, error: ${error}`,
      });
      throw new Error(AUTH_ERROR_MESSAGES.TOKEN_FETCH_FAILED);
    }
    return token;
  }

  // Case 2: Token exists - check if validation is cached
  if (isValidationCacheFresh()) {
    // Token was validated recently, use it directly
    return token;
  }

  // Case 3: Validation cache expired - perform checks
  try {
    // Check token structure
    if (!isTokenStructureValid(token)) {
      console.warn(AUTH_ERROR_MESSAGES.TOKEN_INVALID + ". Fetching new token...");
      token = await fetchAndStoreToken();
      return token;
    }

    // Check token expiration
    const tokenStatus = isTokenExpired(token, TOKEN_CONFIG.EXPIRY_BUFFER_MINUTES);

    // Special case: token expired more than 24 hours ago ("expiredYesterday")
    // We force a clean Okta re-login rather than trying to refresh.
    if (tokenStatus === "expiredYesterday") {
      await redirectToOktaLogin(oktaAuthInstance, AUTH_ERROR_MESSAGES.TOKEN_EXPIRED_YESTERDAY);
      throw new Error(AUTH_ERROR_MESSAGES.TOKEN_EXPIRED_YESTERDAY);
    }

    // Generic case: expired or expiring soon (boolean true) => refresh
    if (tokenStatus === true) {
      console.warn(AUTH_ERROR_MESSAGES.TOKEN_EXPIRED + ". Refreshing...");
      token = await fetchAndStoreToken();
      return token;
    }

    // Token is valid - update cache time
    lastTokenValidationTime = Date.now();
    return token;
    
  } catch (error) {
    console.error("Failed to validate or refresh JWT token:", error);
    await Log({
      errorMessage: `${AUTH_ERROR_MESSAGES.TOKEN_VALIDATION_FAILED}, url: ${requestUrl}, error: ${error}`,
    });
    
    // If validation fails due to auth issue, redirect to login
    if (error instanceof Error && error.message === AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED) {
      await redirectToOktaLogin(oktaAuthInstance, `${AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED} during token validation`);
    }
    
    throw new Error(AUTH_ERROR_MESSAGES.TOKEN_VALIDATION_FAILED);
  }
};

/**
 * Check if error message indicates database user validation issue
 * @param message - Error message from API response
 * @returns true if message matches known DB user error patterns
 */
const isDBUserError = (message: string): boolean => {
  if (!message) return false;
  const lowerMessage = message.toLowerCase();
  return DB_USER_ERROR_PATTERNS.some(err => lowerMessage.includes(err));
};

/**
 * Handle database user validation errors
 * @param errorMessage - Raw error message from API
 * @param url - Request URL that failed
 */
const handleDBUserError = async (errorMessage: string, url: string): Promise<void> => {
  if (!dispatchInstance || !navigateInstance) return;

  console.error("Login Database Level error:", errorMessage);
  await Log({
    errorMessage: `Front-end API failed: ${JSON.stringify(errorMessage)}`,
  });
  dispatchInstance(
    PopoutNotification(
      NOTIFICATION_TYPE.ERROR,
      typeof errorMessage === "string" ? errorMessage : ErrorMessages.dbUserInfoMissing,
      Date.now().toString()
    )
  );
  navigateInstance("/401");
};

/**
 * Handle 401 unauthorized errors with specific error messages
 * @param errorMessage - Lowercase error message from API
 * @param url - Request URL that failed
 * @param error - Original error object
 */
const handle401Error = async (errorMessage: string, url: string, error: any): Promise<void> => {
  if (!dispatchInstance || !navigateInstance) return;

  const lowerMessage = errorMessage.toLowerCase();

  if (lowerMessage.includes("user is not active")) {
    console.error("OKTA user not active");
    await Log({
      errorMessage: `Front-end API failed, url: ${url} - User not active`,
      error,
    });
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        ErrorMessages.userNotActive,
        Date.now().toString()
      )
    );
    navigateInstance("/401");
    return;
  }

  if (lowerMessage.includes("the user email is missing")) {
    console.error("User email is missing");
    await Log({
      errorMessage: `Front-end API failed, url: ${url} - Email missing`,
      error,
    });
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof errorMessage === "string" ? errorMessage : ErrorMessages.userEmailMissing,
        Date.now().toString()
      )
    );
    navigateInstance("/401");
    return;
  }

  if (lowerMessage.includes("jwt expired")) {
    console.error("JWT Token expired in API response");
    await Log({
      errorMessage: `Front-end JWT expired during API call: ${url}`,
    });
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        "Session expired. Please refresh the page.",
        Date.now().toString()
      )
    );
    navigateInstance("/401");
    return;
  }

  // Generic 401 error
  console.error("Unauthorized access");
  navigateInstance("/401");
};

/**
 * Handle 503 service unavailable errors (deployment in progress)
 * @param headers - Response headers
 * @returns true if deployment is in progress and redirect occurred
 */
const handle503Error = (headers: any): boolean => {
  if (!navigateInstance) return false;

  const deploymentStatus = headers?.get?.("X-Deployment-Status") ?? headers?.["x-deployment-status"];
  
  if (deploymentStatus) {
    console.error(AUTH_ERROR_MESSAGES.DEPLOYMENT_IN_PROGRESS);
    navigateInstance("/maintenance");
    return true;
  }
  
  return false;
};

// ============================================================================
// Cross-Tab Synchronization
// ============================================================================

/**
 * Handle storage events for cross-tab token synchronization
 * When token is updated in another tab, clear validation cache
 */
const handleStorageChange = (event: StorageEvent): void => {
  if (event.key === STORAGE_KEYS.AUTH_USER && event.newValue !== event.oldValue) {
    console.log("Token updated in another tab, clearing validation cache");
    clearValidationCache();
  }
};

/**
 * Handle page visibility change
 * Clear validation cache when page becomes visible to force revalidation
 */
const handleVisibilityChange = (): void => {
  if (document.visibilityState === 'visible') {
    console.log("Page became visible, clearing validation cache for fresh token check");
    clearValidationCache();
  }
};

/**
 * Setup event listeners for cross-tab sync and visibility changes
 */
const setupEventListeners = (): void => {
  // Listen for storage events (cross-tab communication)
  globalThis.addEventListener('storage', handleStorageChange);
  
  // Listen for visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Remove event listeners
 */
const cleanupEventListeners = (): void => {
  globalThis.removeEventListener('storage', handleStorageChange);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Cleanup all interceptor resources and event listeners
 * Call this when unmounting the app or cleaning up interceptors
 */
export const cleanupAuthInterceptors = (): void => {
  cleanupEventListeners();
  oktaAuthInstance = null;
  dispatchInstance = null;
  navigateInstance = null;
  isInterceptorInitialized = false;
  console.log("Cleaned up auth interceptors and event listeners");
};

// ============================================================================
// Interceptor Setup
// ============================================================================


/**
 * Initialize Axios authentication interceptors
 * Handles JWT token validation, refresh, and error responses
 * Sets up cross-tab synchronization and visibility change handling
 * @param oktaAuth - OktaAuth instance from Okta React SDK
 * @param dispatch - Redux dispatch function
 * @param navigate - React Router navigate function
 */

function setupRequestInterceptor() {
  axios.interceptors.request.use(
    async (request) => {
      if (isExcludedEndpoint(request.url)) {
        return request;
      }
      try {
        const jwtToken = await ensureValidToken(request.url ?? "unknown");
        _.set(request, "headers.jwt_token", jwtToken);
        return request;
      } catch (error) {
        console.error("Request interceptor error:", error);
        throw error;
      }
    },
    (error) => {
      console.error("Request interceptor error:", error);
      throw error;
    }
  );
}

function dispatchErrorNotification(message: string) {
  dispatchInstance?.(
    PopoutNotification(
      NOTIFICATION_TYPE.ERROR,
      message,
      Date.now().toString()
    )
  );
}

function handleNetworkError(error: Error & { code?: string }) {
  console.error("Network error - no response from server:", error.message);
  if (error.code === "ECONNABORTED") {
    console.error("Request timeout");
    dispatchErrorNotification(AUTH_ERROR_MESSAGES.REQUEST_TIMEOUT);
  } else if (error.message === "Network Error") {
    console.error("Network connectivity issue");
    dispatchErrorNotification(AUTH_ERROR_MESSAGES.NETWORK_ERROR);
  } else {
    console.error("Unknown network error");
  }
  throw error;
}

async function handle403Error(originalRequest: AxiosRequestConfig, error: unknown) {
  console.error("Access forbidden - insufficient permissions");
  await Log({
    errorMessage: `Front-end API failed, url: ${originalRequest?.url ?? "unknown"} - Forbidden (403)`,
    error,
  });
  dispatchErrorNotification(AUTH_ERROR_MESSAGES.ACCESS_DENIED);
  navigateInstance?.("/401");
  throw error;
}

function setupResponseInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config: originalRequest, response } = error;
      if (!response) {
        handleNetworkError(error);
      }
      const status = _.get(response, "status");
      if (!status) {
        console.error("No status code in response");
        throw error;
      }
      const rawErrorMessage =
        response?.data?.error?.message ??
        response?.data?.errors?.[0]?.message ??
        "";
      const errorMessage = rawErrorMessage.toLowerCase?.() ?? "";
      if (status === 503 && handle503Error(response.headers)) {
        throw new Error("Deployment in progress");
      }
      if (isDBUserError(errorMessage)) {
        await handleDBUserError(rawErrorMessage, originalRequest?.url ?? "unknown");
        throw error;
      }
      if (status === 403) {
        await handle403Error(originalRequest, error);
      }
      if (status === 401) {
        await handle401Error(errorMessage, originalRequest?.url ?? "unknown", error);
        throw error;
      }
      console.error("API error:", errorMessage ?? error.message);
      throw error;
    }
  );
}

export const initAxiosAuthInterceptors = (
  oktaAuth: OktaAuth,
  dispatch: Dispatch,
  navigate: NavigateFunction
) => {
  if (isInterceptorInitialized) {
    console.warn("Interceptors already initialized, skipping");
    return;
  }
  oktaAuthInstance = oktaAuth;
  dispatchInstance = dispatch;
  navigateInstance = navigate;
  setupEventListeners();
  console.log("Initializing axios auth interceptors with cross-tab sync");
  setupRequestInterceptor();
  setupResponseInterceptor();
  isInterceptorInitialized = true;
};
